import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Download, GraduationCap, Users } from "lucide-react";
import { PageHeader } from "@/components/app/PageHeader";

function monthNow() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function ReportsPage() {
  const { t, lang } = useI18n();
  const [month, setMonth] = useState(monthNow());
  const [busy, setBusy] = useState(null);

  const download = async (kind) => {
    setBusy(kind);
    try {
      const res = await api.get(`/reports/attendance/${kind}.csv`, {
        params: { month },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${kind}-attendance-${month}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      const text = err.response?.data ? await err.response.data.text?.() : "";
      let detail = text;
      try { detail = JSON.parse(text).detail; } catch { /* ignore */ }
      toast.error(formatApiErrorDetail(detail) || "Download failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-reports">
      <PageHeader
        eyebrow={t("admin_dashboard_title")}
        title={t("reports")}
        subtitle={lang === "ms" ? "Muat turun rekod kehadiran bulanan sebagai CSV." : "Download monthly attendance records as CSV."}
      />

      <Card className="pmm-pop max-w-md rounded-[1.5rem] border-border/60 shadow-lg" style={{ animationDelay: "0.1s" }}>
        <CardContent className="space-y-3 p-6">
          <Label className="text-xs font-bold uppercase tracking-widest text-blue-950/70">{t("month")}</Label>
          <Input
            data-testid="report-month"
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="num h-12 rounded-xl text-lg font-bold text-blue-950"
          />
          <p className="num text-xs text-muted-foreground">{month}</p>
        </CardContent>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <Card className="pmm-pop group overflow-hidden rounded-[1.5rem] border-border/60 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" style={{ animationDelay: "0.18s" }}>
          <div className="h-1.5 w-full bg-gradient-to-r from-orange-600 to-yellow-400" />
          <CardContent className="p-6">
          <div className="mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br from-orange-600 to-orange-400 text-white shadow-lg shadow-orange-500/30 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6">
            <GraduationCap className="h-6 w-6" />
          </div>
          <h2 className="font-display text-lg font-extrabold uppercase tracking-wide text-blue-950">{t("students")} {t("attendance")}</h2>
          <p className="mt-1 text-sm font-medium text-muted-foreground">
            {lang === "ms" ? "Semua tanda hadir/lewat/tidak hadir untuk bulan ini." : "All present/late/absent marks for the month."}
          </p>
          <Button
            onClick={() => download("students")}
            disabled={busy === "students"}
            data-testid="dl-students-csv"
            className="mt-4 h-11 gap-2 rounded-full bg-gradient-to-r from-orange-600 to-orange-500 px-6 font-display text-sm font-extrabold uppercase tracking-wide text-white shadow-lg shadow-orange-500/40 transition-transform duration-300 hover:scale-105"
          >
            <Download className="h-4 w-4" /> {t("downloadCsv")}
          </Button>
        </CardContent></Card>

        <Card className="pmm-pop group overflow-hidden rounded-[1.5rem] border-border/60 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" style={{ animationDelay: "0.26s" }}>
          <div className="h-1.5 w-full bg-gradient-to-r from-blue-900 to-blue-500" />
          <CardContent className="p-6">
          <div className="mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-br from-blue-900 to-blue-600 text-white shadow-lg shadow-blue-800/30 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6">
            <Users className="h-6 w-6" />
          </div>
          <h2 className="font-display text-lg font-extrabold uppercase tracking-wide text-blue-950">{t("staff")} & {t("teacher")} {t("attendance")}</h2>
          <p className="mt-1 text-sm font-medium text-muted-foreground">
            {lang === "ms" ? "Rekod daftar masuk/keluar staf dan guru." : "Staff and teacher check-in / check-out records."}
          </p>
          <Button
            onClick={() => download("staff")}
            disabled={busy === "staff"}
            data-testid="dl-staff-csv"
            className="mt-4 h-11 gap-2 rounded-full bg-gradient-to-r from-blue-900 to-blue-700 px-6 font-display text-sm font-extrabold uppercase tracking-wide text-white shadow-lg shadow-blue-800/40 transition-transform duration-300 hover:scale-105"
          >
            <Download className="h-4 w-4" /> {t("downloadCsv")}
          </Button>
        </CardContent></Card>
      </div>
    </div>
  );
}
